import { useTranslation } from 'react-i18next';

import { Card } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';

import { CheckpointStatus, type TimelineCheckpoint } from '../../types/experiment.types';

import { ExperimentTimelineItem } from './experiment-timeline-item';
import { ExperimentTimelinePlaceholder } from './experiment-timeline-placeholder';
import { TimelineRowFrame } from './experiment-timeline-row-frame';

interface Props {
  checkpoints: TimelineCheckpoint[] | undefined;
  /** Owning experiment — needed by the Day 28 row's readout download actions. */
  experimentId: string;
  isLoading: boolean;
  isError?: boolean;
}

export function ExperimentTimeline({
  checkpoints,
  experimentId,
  isLoading,
  isError = false,
}: Props) {
  const { t } = useTranslation('experiments');

  return (
    <Card className="overflow-hidden py-0">
      {/* Column header — icon column is empty */}
      <div className="bg-muted/50 text-muted-foreground grid grid-cols-[88px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,3fr)] gap-4 border-b px-6 py-3 text-sm font-medium">
        <span />
        <span>{t('detail.columns.checkpoint')}</span>
        <span>{t('detail.columns.status')}</span>
        <span>{t('detail.columns.details')}</span>
      </div>

      {/* Placeholder (not the terse "data unavailable" line) whenever the
          endpoint errors / 404s OR returns an empty list — a pre-launch run
          that isn't reporting yet still gets a timeline-shaped frame, never
          fabricated checkpoints. */}
      {isError || (!isLoading && checkpoints?.length === 0) ? (
        <ExperimentTimelinePlaceholder />
      ) : isLoading || !checkpoints ? (
        <div>
          {Array.from({ length: 4 }).map((_, idx) => (
            <TimelineRowFrame key={idx}>
              <Skeleton className="h-12 w-12 justify-self-center rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-4 w-40" />
              </div>
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-4 w-full" />
            </TimelineRowFrame>
          ))}
        </div>
      ) : (
        <div>
          {checkpoints.map((checkpoint, idx) => {
            const prev = checkpoints[idx - 1];
            return (
              <ExperimentTimelineItem
                key={checkpoint.id}
                checkpoint={checkpoint}
                experimentId={experimentId}
                isFirst={idx === 0}
                isLast={idx === checkpoints.length - 1}
                topLineCompleted={prev?.status === CheckpointStatus.PASSED}
                bottomLineCompleted={checkpoint.status === CheckpointStatus.PASSED}
              />
            );
          })}
        </div>
      )}
    </Card>
  );
}
